import type { PayloadAction } from "@reduxjs/toolkit";
import { createAppSlice } from "../createAppSlice";
import axo from "@/lib/axo";
import { hasCookie, setCookie, getCookie } from "cookies-next/client";
import { AppDispatch } from "..";

export interface CartSliceState {
  cartItems: CartItem[];
  cartSummary: CartSummary;
  cartOffers: CartOffer[];
  status: "idle" | "loading" | "failed";
  guestId?: string;
  message?: string;
}

const initialState: CartSliceState = {
  cartItems: [],
  cartSummary: {} as CartSummary,
  cartOffers: [],
  status: "idle",
  message: "",
};

type AddToCardParams = {
  serviceId: number;
  eventId: number;
  eventDetailId: number;
  quantity: number;
  countryCode: string;
  guestUserId?: string;
};

// If you are not using async thunks you can use the standalone `createSlice`.
export const cartSlice = createAppSlice({
  name: "cart",
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: (create) => ({
    setCartItems: create.reducer((state, action: PayloadAction<CartItem[]>) => {
      state.cartItems = action.payload;
    }),
    setCartSummary: create.reducer(
      (state, action: PayloadAction<CartSummary>) => {
        state.cartSummary = action.payload;
      }
    ),
    setCartOffers: create.reducer(
      (state, action: PayloadAction<CartOffer[]>) => {
        state.cartOffers = action.payload;
      }
    ),

    addCartItem: create.asyncThunk(
      async (params: AddToCardParams, thunkApi) => {
        const { data } = await axo.post("/add-cart-item", { ...params });
        const dispatch = thunkApi.dispatch as AppDispatch;
        dispatch(
          cartSlice.actions.getItems({
            countryCode: params.countryCode,
            guestUserId: params.guestUserId,
          })
        );
        return data;
      },
      {
        pending: (state) => {
          state.status = "loading";
        },
        fulfilled: (state, action) => {
          state.status = "idle";
          state.message = action.payload?.message;
          state.guestId = action.payload?.guestId;
          setCookie("guestId", action.payload?.guestId);
        },
        rejected: (state) => {
          state.status = "failed";
        },
      }
    ),
    changeQuantity: create.asyncThunk(
      async (
        params: {
          cartId: number;
          countryCode: string;
          quantity: number | null;
        },
        thunkApi
      ) => {
        const { data } = await axo.put("/update-cart-item", { ...params });
        const dispatch = thunkApi.dispatch as AppDispatch;
        await dispatch(
          cartSlice.actions.getItems({
            countryCode: params.countryCode,
            guestUserId: hasCookie("guestId") ? getCookie("guestId") : null,
          })
        );
        return data;
      },
      {
        pending: (state) => {
          state.status = "loading";
        },
        fulfilled: (state, action) => {
          state.status = "idle";
          state.message = action.payload?.message;
        },
        rejected: (state) => {
          state.status = "failed";
        },
      }
    ),
    removeIem: create.asyncThunk(
      async (
        params: {
          cartId: number;
          countryCode: string;
        },
        thunkApi
      ) => {
        const { data } = await axo.delete("/remove-cart-item", {
          params: { ...params },
        });
        const dispatch = thunkApi.dispatch as AppDispatch;
        dispatch(
          cartSlice.actions.getItems({
            countryCode: params.countryCode,
            guestUserId: hasCookie("guestId") ? getCookie("guestId") : null,
          })
        );
        return data;
      },
      {
        pending: (state) => {
          state.status = "loading";
        },
        fulfilled: (state, action) => {
          state.status = "idle";
          state.message = action.payload?.message;
        },
        rejected: (state) => {
          state.status = "failed";
        },
      }
    ),
    getItems: create.asyncThunk(
      async (params: {
        countryCode: string;
        guestUserId: any;
        couponCode?: string;
      }) => {
        const { data } = await axo.get("/get-cart-item", {
          params: { ...params },
        });
        return data;
      },
      {
        pending: (state) => {
          state.status = "loading";
        },
        fulfilled: (state, action) => {
          state.status = "idle";
          state.message = action.payload?.message;
          state.cartItems = action.payload?.cartItems;
          state.cartSummary = action.payload?.cartSummary;
          state.cartOffers = action.payload?.offers;
        },
        rejected: (state) => {
          state.status = "failed";
        },
      }
    ),
  }),
  selectors: {
    getCartItems: (state) => state.cartItems,
    getCartOffers: (state) => state.cartOffers,
    getCartSummary: (state) => state.cartSummary,
    status: (state) => state.status,
  },
});

// Action creators are generated for each case reducer function.
export const {
  setCartItems,
  setCartOffers,
  setCartSummary,
  addCartItem,
  changeQuantity,
  getItems,
  removeIem,
} = cartSlice.actions;

// Selectors returned by `slice.selectors` take the root state as their first argument.
export const { getCartItems, getCartOffers, getCartSummary, status } =
  cartSlice.selectors;
